/**
 * Parse a UTC datetime string from SQLite (e.g. "2024-06-15 10:30:00")
 * as an unambiguous UTC Date object.
 *
 * SQLite datetime('now') always stores UTC but uses a space separator
 * and no trailing 'Z', so JavaScript's Date constructor treats it as
 * local time in many browsers.  Replacing the space with 'T' and
 * appending 'Z' makes it an ISO-8601 UTC string that every browser
 * parses correctly, so toLocaleString/toLocaleDateString will then
 * apply the user's local timezone (including BST in summer).
 *
 * Date-only fields ("2024-06-15") are already handled correctly by
 * the browser — do NOT pass them through this function.
 */
export function fromUTC(str) {
  if (!str) return null;
  // Already has a timezone indicator — don't double-process
  if (str.endsWith('Z') || str.includes('+')) return new Date(str);
  // "2024-06-15 10:30:00"  →  "2024-06-15T10:30:00Z"
  return new Date(str.replace(' ', 'T') + 'Z');
}

/**
 * Format a UTC DB timestamp as a short date+time in British locale.
 * e.g. "15 Jun, 10:30"
 */
export function fmtDateTime(str) {
  const d = fromUTC(str);
  if (!d) return '';
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/**
 * Format a UTC DB timestamp as a short date+time including year.
 * e.g. "15 Jun 2024, 10:30"
 */
export function fmtDateTimeLong(str) {
  const d = fromUTC(str);
  if (!d) return '';
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * Format a UTC DB timestamp as date only (no time).
 * e.g. "15 Jun 2024"
 */
export function fmtDateOnly(str) {
  const d = fromUTC(str);
  if (!d) return '';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
